import React, { Component } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Select from 'components/Common/Form/Input/Select'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextInput from 'components/Common/Form/Input/TextInput'
import { formToObject } from 'lib/forms/formToObject'
import { connect } from 'react-redux'
import TextArea from 'components/Common/Form/Input/TextArea'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { sendEnquiryRequest } from 'api/notification'
import { getSupportEmailForCurrentRegion } from 'selectors/supportContactSelector'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import { arrayToObject } from 'lib/array/arrayUtils'

type IssueOption = 'purchase' | 'purchased' | 'somethingElse'

const issuesDataArray: Array<{ option: IssueOption, text: string }> = [
  {
    option: 'purchase',
    text: 'I have a question about a purchase I want to make',
  },
  {
    option: 'purchased',
    text: 'I have a question about a purchase I made on Luxury Escapes',
  },
  {
    option: 'somethingElse',
    text: 'I have a question about something else',
  },
]

const issuesData = arrayToObject(issuesDataArray, i => i.option)

const subIssuesData: Record<IssueOption, Array<{ option: string, text: string }>> = {
  purchase: [
    {
      option: 'bookingAssistance',
      text: 'Booking Assistance',
    },
    {
      option: 'quoteRequest',
      text: 'Quote Request',
    },
  ],
  purchased: [
    {
      option: 'changeBooking',
      text: 'Change Booking',
    },
    {
      option: 'cancelBooking',
      text: 'Cancel Booking',
    },
    {
      option: 'generalEnquiries',
      text: 'General Enquiries',
    },
    {
      option: 'complaint',
      text: 'Complaint',
    },
    {
      option: 'positiveFeedback',
      text: 'Positive Feedback',
    },
  ],
  somethingElse: [
    {
      option: 'changeDetails',
      text: 'I want to change my details (name, contact number, email address)',
    },
    {
      option: 'forgotPassword',
      text: 'I have forgotten my password',
    },
    {
      option: 'leCredits',
      text: 'I have a question about my Luxury Escapes credits',
    },
    {
      option: 'unsubscribe',
      text: 'I want to unsubscribe from Luxury Escapes emails',
    },
    {
      option: 'changeSubscription',
      text: 'I want to change my email subscription frequency',
    },
    {
      option: 'otherQuestion',
      text: 'I have a question that you guys have not thought of yet!',
    },
  ],
}

const Root = styled(Pane)`
  margin-top: ${rem(50)};

  > * + * {
    margin-top: ${rem(24)};
  }
`

const Form = styled.form`
  > * + * {
    margin-top: ${rem(12)};
  }
`

const EnquiryInput = styled(TextArea)`
  resize: vertical;
`

// TEXT TODO: confirm with design
const TextAreaMinimumText = styled(Caption)`
  color: ${props => props.theme.palette.neutral.default.four};
  margin-top: ${rem(-20)};
`

interface ContactForm {
  issue: IssueOption
  subIssue?: string
  email: string
  name: string
  phone: string
  enquiry: string
  orderId?: string
}

interface State {
  issue?: { option: IssueOption, text: string }
  sending?: boolean
  success?: boolean
  error?: boolean
}

interface Props {
  email: string
  regionCode: string
}

class ContactUsForm extends Component<React.PropsWithChildren<Props>, State> {
  state: State = {}

  onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject<ContactForm>(e.currentTarget)
    const formObject = {
      ...values,
      issue: issuesData[values.issue],
      subIssue: subIssuesData[values.issue].find(i => i.option === values.subIssue),
    }

    this.setState({ sending: true, error: false })
    sendEnquiryRequest(formObject, this.props.regionCode)
      .then(() => this.setState({ success: true }))
      .catch(() => this.setState({ error: true }))
      .finally(() => this.setState({ sending: false }))
  }

  onIssueSelect = (e: React.FormEvent<HTMLSelectElement>) => {
    const issue = issuesData[e.currentTarget.value as IssueOption]
    this.setState({ issue })
  }

  render() {
    const { issue, error, sending, success } = this.state
    const { email } = this.props
    const subIssues = issue ? subIssuesData[issue.option] : undefined

    return (
      <Root id="contact-form" background="neutral-six">
        <Heading variant="heading3">
          Can't find what you're looking for? Submit an enquiry
        </Heading>
        {success && <MessageBanner kind="success">
          Thank you! Your enquiry has been submitted successfully. A member of our team will be in touch to assist you.
        </MessageBanner>}
        <Form name="contactUs" onSubmit={this.onFormSubmit}>
          <div>
            <Select
              name="issue"
              label="What can we do to help today?"
              placeholder="Please select your issue"
              onChange={this.onIssueSelect}
              requiredErrorMessage="Please select an issue"
              required
            >
              {issuesDataArray.map((selection) =>
                <option key={selection.option} value={selection.option}>
                  {selection.text}
                </option>,
              )}
            </Select>
            {subIssues && issue &&
              <Select
                label={issue.text}
                name="subIssue"
                placeholder="Please select an answer"
                required
                requiredErrorMessage="Please select an option for your issue"
              >
                {subIssues.map((selection) =>
                  <option
                    key={selection.option}
                    value={selection.option}
                  >
                    {selection.text}
                  </option>,
                )}
              </Select>}
            <TextInput
              label="Email Address"
              type="email"
              name="email"
              required
              invalidErrorMessage="Please enter a valid email address"
            />
            <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
              <TextInput
                label="Name"
                type="name"
                name="name"
                required
                invalidErrorMessage="Please enter your name"
              />
              <TextInput
                required
                type="tel"
                name="phone"
                label="Phone"
                invalidErrorMessage="Please enter a valid phone number"
              />
            </Group>
            {issue?.option === 'purchased' &&
              <TextInput
                type="text"
                name="orderId"
                pattern="[a-zA-Z0-9]{6}"
                maxLength={6}
                minLength={6}
                label="Order Number"
                invalidErrorMessage="Please enter a valid order number"
              />}
            <EnquiryInput
              label="Please provide more details below - including accommodation, tour or booking information"
              required
              name="enquiry"
              minLength={30}
              cols={5}
              invalidErrorMessage="Please provide at least 30 characters to describe your issue"
            />
            <TextAreaMinimumText variant="medium" align="end">
              minimum of 30 characters
            </TextAreaMinimumText>
          </div>
          {error && <MessageBanner
            kind="critical"
            heading="Oops, looks like there has been an error submitting your enquiry."
          >
            If you continue to experience issues, please try again later or send us an email at <TextLink href={'mailto:' + email}>{email}</TextLink>.
          </MessageBanner>}
          <TextButton
            kind="primary"
            disabled={sending || success}
            fit="flex"
            size="large"
            type="submit"
          >
            {sending && 'Submitting...'}
            {!sending && (success ? 'Thank you' : 'Submit')}
          </TextButton>
        </Form>
      </Root>
    )
  }
}

function mapStateToProps(state: App.State) {
  return {
    email: getSupportEmailForCurrentRegion(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(ContactUsForm)
